import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { SectionHeading } from "../components/shared/SectionHeading";
import { siteContent, images } from "../data/content";

export const Packaging = () => {
  return (
    <div>
      {/* Hero */}
      <section className="py-16 md:py-24 bg-charcoal-paper border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="font-mono text-coconut text-sm tracking-wider uppercase mb-2 block">
              Private Label Services
            </span>
            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-white mb-6"
              data-testid="packaging-title"
            >
              Packaging & Private Label
            </h1>
            <p className="text-lg text-zinc-400">
              Launch your own charcoal brand with our complete private label service. 
              From design to delivery, we handle everything.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Workflow */}
      <section className="py-24 bg-charcoal">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <SectionHeading
            title="How It Works"
            subtitle="Our streamlined process to bring your branded product to market"
          />

          <div className="grid md:grid-cols-5 gap-4">
            {siteContent.privateLabelWorkflow.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-charcoal-paper border border-white/5 p-6 h-full">
                  <div className="w-10 h-10 bg-coconut text-charcoal flex items-center justify-center font-mono font-bold mb-4">
                    {step.step}
                  </div>
                  <h4 className="font-heading text-white uppercase text-sm mb-2">{step.title}</h4>
                  <p className="text-zinc-500 text-sm">{step.description}</p>
                </div>
                {index < siteContent.privateLabelWorkflow.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-4 h-4 text-coconut" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Packaging Options */}
      <section className="py-24 bg-charcoal-paper">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="font-mono text-coconut text-sm tracking-wider uppercase mb-2 block">
                Customization Options
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white mb-6">
                Your Brand, Your Way
              </h2>
              <div className="space-y-4 mb-8">
                {siteContent.privateLabelFeatures.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-coconut/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-coconut" />
                    </div>
                    <span className="text-zinc-300">{item}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4">
                <Button
                  asChild
                  className="bg-[#25D366] text-white hover:bg-[#1da851] font-heading uppercase"
                  data-testid="packaging-wa-artwork-btn"
                >
                  <a
                    href={`${siteContent.contact.whatsappLink}?text=${encodeURIComponent("Hi, I'd like to discuss private label packaging and send my artwork.")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MessageCircle className="mr-2 w-4 h-4" />
                    Send Artwork via WhatsApp
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/5 font-heading uppercase"
                  data-testid="packaging-quote-btn"
                >
                  <Link to="/contact">Request Quote</Link>
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="aspect-square bg-charcoal border border-white/5 overflow-hidden col-span-2">
                <img src={images.charcoalGlow} alt="Premium charcoal briquettes" className="w-full h-full object-cover" />
              </div>
              <div className="aspect-square bg-charcoal border border-white/5 overflow-hidden col-span-2">
                <img src={images.hookah} alt="Hookah charcoal branding" className="w-full h-full object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Package Mockups */}
      <section className="py-24 bg-charcoal noise-overlay">
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
          <SectionHeading
            title="Packaging Samples"
            subtitle="Examples of packaging configurations we can provide"
          />

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { title: "1kg Inner Box", desc: "Retail-ready packaging" },
              { title: "2kg Inner Box", desc: "Bulk retail option" },
              { title: "Master Carton 10kg", desc: "10 x 1kg configuration" },
              { title: "Custom Configuration", desc: "Tailored to your needs" },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-charcoal-paper border border-white/5 p-6 text-center"
              >
                <div className="aspect-square bg-charcoal-subtle border border-white/10 flex items-center justify-center mb-4">
                  <span className="text-zinc-600 text-sm font-mono">Mockup Placeholder</span>
                </div>
                <h4 className="font-heading text-white uppercase text-sm mb-1">{item.title}</h4>
                <p className="text-zinc-500 text-xs">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-coconut">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight text-charcoal mb-6">
            Ready to Create Your Brand?
          </h2>
          <p className="text-charcoal/80 mb-8 max-w-2xl mx-auto">
            Send us your artwork and requirements. We'll create mockups and provide a detailed quote.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              asChild
              size="lg"
              className="bg-charcoal text-white hover:bg-charcoal-paper font-heading uppercase"
            >
              <Link to="/contact">Request a Quote</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-charcoal text-charcoal hover:bg-charcoal/10 font-heading uppercase"
            >
              <a
                href={`${siteContent.contact.whatsappLink}?text=${encodeURIComponent("Hi, I'm interested in private label packaging services.")}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <MessageCircle className="mr-2 w-4 h-4" />
                WhatsApp Us
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
